import { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import { useDispatch } from 'react-redux'
import { Box, Button, Container } from '@mui/material'

import QuizService from '@/services/QuizService'
import { updateQuiz as updateQuizAction } from '@/store/quizzes/actions'

import takingNotesImgFull from '@/assets/New-images/taking-notes-img-full.svg'
import Gcheckimg from '@/assets/New-images/green-check.svg'
import Gerrorimg from '@/assets/New-images/g-error.svg'

const quizService = new QuizService()

const POLLING_INTERVAL = 15 * 1000 // 15 seconds

interface LocationState {
  quizId?: string
  group?: string
  selectedFileType?: string
  selectedFile?: any
  selectedFileName?: string
  through?: string
  school?: string
}

export const QuizExtractionProcessing = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const { t } = useTranslation('translation')
  const { jobId } = useParams<{ jobId: string }>()
  const queryParams = new URLSearchParams(location.search)

  const [status, setStatus] = useState<'processing' | 'completed' | 'failed'>(
    'processing',
  )
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [documentName, setDocumentName] = useState<string>('')
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const locationState = (location.state as LocationState) || {}
  const quizId = queryParams.get('quizId') || locationState.quizId
  const group = queryParams.get('group') || locationState.group
  const through = queryParams.get('through') || locationState.through
  const selectedFileType = locationState.selectedFileType
  const selectedFile = locationState.selectedFile
  const selectedFileName = locationState.selectedFileName
  const school = locationState.school

  // Set document name from location state or use default
  useEffect(() => {
    if (selectedFileName) {
      setDocumentName(selectedFileName)
    } else {
      setDocumentName(t('Quiz Document'))
    }
  }, [selectedFileName, t])

  const handleGeneratedQuestions = async (questions: any[]) => {
    const groupQuery = group ? `?group=${group}` : ''

    const quizArray: any = { questions: [] }
    questions.forEach((element: any) => {
      const quiz = {
        ...element,
        id: element.id ? element.id : uuidv4(),
        ideal_answer: element.ideal_answer === null ? '' : element.ideal_answer,
        text: element.text
          ? element.text.replaceAll('\n', '<br>')
          : element.text,
      }
      quizArray.questions.push(quiz)
    })

    if (through === 'create-quiz') {
      // Coming from ImportQuiz flow
      if (selectedFileType === 'customtemplates') {
        history.push('/quizzes/edit/' + quizId + '?fromQuizExtraction=true', {
          selectedFileType: selectedFileType === 'customtemplates',
          quizArray,
          selectedFile,
          fromQuizExtraction: true,
        })
        return
      }

      try {
        await dispatch(updateQuizAction(quizId, quizArray))
        history.push('/quizzes/edit/' + quizId + '?fromQuizExtraction=true', {
          selectedFileType: true,
          quizArray,
          fromQuizExtraction: true,
        })
      } catch (e) {
        console.error('Failed to update quiz', e)
      }
    } else {
      // Coming from ImportQuestionBank flow - redirect to question bank editor
      history.push('/import-questions-bank/questions-review' + groupQuery, {
        selectedFileType: 'customtemplates',
        quizArray,
        createFromTestFile: true,
        group,
        school,
        fromQuizExtraction: true,
      })
    }
  }

  const checkJobStatus = async () => {
    try {
      const response = await quizService.getQuizExtractionJob(jobId)
      const job = response?.data?.data?.quiz_extraction_job

      if (!job) {
        setStatus('failed')
        setErrorMessage(t('Job not found. Please try again.'))
        stopPolling()
        return
      }

      if (job.status === 'completed') {
        setStatus('completed')
        stopPolling()
        handleGeneratedQuestions(job.questions || [])
      } else if (job.status === 'failed') {
        setStatus('failed')
        setErrorMessage(
          t('Failed to extract questions from the document. Please try again.'),
        )
        stopPolling()
      }
      // If still 'new' or other status, continue polling
    } catch (error) {
      console.error('Error checking job status:', error)
      setStatus('failed')
      setErrorMessage(
        t('Something went wrong while checking the extraction status.'),
      )
      stopPolling()
    }
  }

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }

  const startPolling = () => {
    // Check immediately
    checkJobStatus()
    // Then poll every 15 seconds
    pollingIntervalRef.current = setInterval(checkJobStatus, POLLING_INTERVAL)
  }

  const handleGoBack = () => {
    history.goBack()
  }

  const renderJobStatusIndicator = () => {
    if (status === 'failed') {
      return (
        <div className="quick-start-box p-4 d-flex align-items-center text-left">
          <div className="doc-success-msg d-flex align-items-center font-600 clr-red font-sm">
            <img src={Gerrorimg} alt="" className="mr-2" />
          </div>
          <div className="d-flex flex-column">
            <span className="clr-red font-18-px font-weight-bold mb-1">
              {documentName}
            </span>
            <span className="clr-grey-txt7">{t('Extraction Failed')}</span>
          </div>
        </div>
      )
    } else if (status === 'processing') {
      return (
        <div className="quick-start-box p-4 d-flex align-items-center text-left">
          <span className="doc-loader spinner-width-48 mr-2 doc-spinner"></span>
          <div className="d-flex flex-column">
            <span className="clr-primary font-18-px font-weight-bold mb-1">
              {documentName}
            </span>
            <span className="clr-grey-txt7">
              {t('Extracting questions...')}
            </span>
          </div>
        </div>
      )
    } else {
      return (
        <div className="quick-start-box p-4 d-flex align-items-center text-left">
          <div className="doc-success-msg d-flex align-items-center font-600 clr-green font-sm">
            <img src={Gcheckimg} alt="" className="mr-2" />
          </div>
          <div className="d-flex flex-column">
            <span className="clr-green font-18-px font-weight-bold mb-1">
              {documentName}
            </span>
            <span className="clr-grey-txt7">{t('Extraction Completed')}</span>
          </div>
        </div>
      )
    }
  }

  useEffect(() => {
    if (jobId) {
      startPolling()
    }

    return () => {
      stopPolling()
    }
  }, [jobId])

  return (
    <section className="page-mid-wraper h-without-foter setup-process">
      <Container>
        <div className="d-flex flex-wrap justify-content-center clr-primary">
          <h1 className="mb-2 font-weight-bold">{t('Processing Document')}</h1>
        </div>
        <div className="mb-32 font-16 text-center font-600">
          {t('Extracting quiz questions from your document')}
        </div>

        <Box className="pdf-section-wrapper mb-32">
          <Box className="row process-documents align-items-start">
            <Box className="col-md-4 col-lg-3">
              <img src={takingNotesImgFull} height={'450'} alt={t('')} />
            </Box>
            <Box className="col-md-8 col-lg-9">
              <p className="font-600 font-16 text-left mb-32">
                {t(
                  'Please stay on this page while we extract the quiz questions from your document. This may take a few minutes.',
                )}
              </p>

              <Box className="mb-32 step-process">
                {renderJobStatusIndicator()}
              </Box>

              {status === 'failed' && errorMessage && (
                <Box className="mb-32">
                  <div className="alert alert-danger" role="alert">
                    {errorMessage}
                  </div>
                  <Button
                    variant="contained"
                    className="btn btn-primary"
                    onClick={handleGoBack}
                  >
                    {t('Go Back')}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Container>
    </section>
  )
}

export default QuizExtractionProcessing
